/**
 * Supervised Paper Test — Owner one-click flow.
 *
 * Single "RUN SUPERVISED PAPER TEST" button that orchestrates 17 named
 * phases end-to-end. Polls the backend every 2s while running. On
 * completion, shows either a rich PASS summary or a FAIL card with the
 * exact step + reason + recommended action.
 *
 * When a workspace ``deploy_block`` is outstanding (previous canonical
 * live failure), the orchestrator returns ``overall=BLOCKED`` and the
 * failure card offers an "Acknowledge & Retry" button that clears the
 * block and starts a fresh run in one action.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_TONE = {
  PASS: "text-emerald-500", FAIL: "pnl-down",
  RUNNING: "text-signal-yellow", WAITING: "text-muted-foreground",
  SKIP: "text-muted-foreground",
};

const STATUS_ICON = {
  PASS: "✓", FAIL: "✗", RUNNING: "→",
  WAITING: "○", SKIP: "○",
};

function _fmt2(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return "$" + Number(v).toLocaleString(undefined,
    { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

async function openAuthedReport(runId, fmt = "html") {
  try {
    const resp = await api.get(`/harness/runs/${runId}/report?fmt=${fmt}`,
                                        { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch {
    toast.error("Report not yet available");
  }
}

export default function SupervisedTestPanel() {
  const [steps, setSteps] = useState([]);         // catalogue
  const [activeRun, setActiveRun] = useState(null);
  const [recent, setRecent] = useState([]);
  const [starting, setStarting] = useState(false);

  const loadCatalogue = useCallback(async () => {
    try {
      const { data } = await api.get("/harness/supervised-paper-test/steps");
      setSteps(data.steps || []);
    } catch { /* ignore */ }
  }, []);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await api.get(
        "/harness/supervised-paper-test/recent?limit=10");
      setRecent(data.runs || []);
      // Reattach to an in-progress run if we reloaded the page.
      const running = (data.runs || []).find(r => r.status === "running");
      if (running && !activeRun) setActiveRun(running);
    } catch { /* ignore */ }
  }, [activeRun]);

  useEffect(() => { loadCatalogue(); loadRecent(); }, [loadCatalogue, loadRecent]);

  // Poll every 2s while a run is active.
  useEffect(() => {
    if (!activeRun || activeRun.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(
          `/harness/supervised-paper-test/${activeRun.id}`);
        setActiveRun(data);
        if (data.status !== "running") loadRecent();
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(t);
  }, [activeRun, loadRecent]);

  const run = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/harness/supervised-paper-test");
      toast.success("Supervised test started");
      const { data: full } = await api.get(
        `/harness/supervised-paper-test/${data.run_id}`);
      setActiveRun(full);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start supervised test");
    } finally { setStarting(false); }
  };

  const acknowledgeAndRetry = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(
        "/harness/supervised-paper-test/acknowledge-and-retry");
      toast.success("Block acknowledged — retrying");
      const { data: full } = await api.get(
        `/harness/supervised-paper-test/${data.run_id}`);
      setActiveRun(full);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Acknowledge failed");
    } finally { setStarting(false); }
  };

  const displaySteps = activeRun?.steps || steps.map(s => ({ ...s, status: "WAITING" }));
  const completed = displaySteps.filter(s =>
    s.status === "PASS" || s.status === "FAIL" || s.status === "SKIP").length;
  const total = displaySteps.length || 17;
  const percent = Math.round((completed / total) * 100);

  const isBlocked = activeRun?.overall === "BLOCKED"
                          || activeRun?.failure?.acknowledgeable;
  const isRunning = activeRun?.status === "running";
  const isPass = activeRun?.overall === "PASS";
  const isFail = activeRun?.overall === "FAIL";

  return (
    <div className="space-y-4" data-testid="supervised-test-panel">
      {/* Top card */}
      <div className="border border-border/80 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="label-cap">Supervised Paper Test</div>
            <div className="text-xl font-medium mt-1">
              {isRunning ? "TEST RUNNING"
                : isPass ? "LAST TEST PASSED"
                : isBlocked ? "BLOCKED"
                : isFail ? "LAST TEST FAILED"
                : "READY FOR SUPERVISED TEST"}
            </div>
            {activeRun?.current_step && isRunning && (
              <div className="text-sm text-muted-foreground mt-1">
                Current step: {
                  displaySteps.find(s => s.key === activeRun.current_step)?.label
                  || activeRun.current_step}
              </div>
            )}
          </div>
          <div>
            {isBlocked ? (
              <button
                onClick={acknowledgeAndRetry}
                disabled={starting}
                className="bg-signal-yellow text-black px-4 py-2 text-sm font-medium uppercase tracking-widest hover:opacity-90 disabled:opacity-50"
                data-testid="supervised-ack-retry-btn">
                Acknowledge & Retry
              </button>
            ) : (
              <button
                onClick={run}
                disabled={starting || isRunning}
                className="bg-emerald-500 text-black px-4 py-2 text-sm font-medium uppercase tracking-widest hover:opacity-90 disabled:opacity-50"
                data-testid="supervised-run-btn">
                {starting ? "Starting…"
                  : isRunning ? "Running…"
                  : "Run Supervised Paper Test"}
              </button>
            )}
          </div>
        </div>
        {activeRun && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mono mb-1">
              <span>{completed} / {total} steps · {percent}%</span>
              {activeRun.duration_ms != null && (
                <span>Duration: {(activeRun.duration_ms / 1000).toFixed(1)}s</span>
              )}
            </div>
            <div className="h-1 bg-border/40">
              <div
                className={`h-full transition-all ${
                  isFail ? "bg-red-500"
                  : isBlocked ? "bg-signal-yellow"
                  : isPass ? "bg-emerald-500"
                  : "bg-signal-yellow"
                }`}
                style={{ width: `${percent}%` }}/>
            </div>
          </div>
        )}
      </div>

      {/* Pass summary */}
      {isPass && activeRun.result_summary && (
        <PassSummary summary={activeRun.result_summary}
                          onView={() => openAuthedReport(activeRun.result_summary.harness_run_id, "html")}/>
      )}

      {/* Failure card */}
      {(isFail || isBlocked) && activeRun.failure && (
        <FailureCard failure={activeRun.failure}
                          harnessRunId={activeRun.harness_run_id}
                          isBlocked={isBlocked}
                          onRetry={run}
                          onAckRetry={acknowledgeAndRetry}/>
      )}

      {/* Step list — always visible */}
      <div className="border border-border/60">
        <div className="p-2 label-cap border-b border-border/60">Steps</div>
        <div className="p-2 space-y-0.5 text-sm">
          {displaySteps.map(s => (
            <div key={s.key} className="grid grid-cols-[20px_1fr_auto] gap-2 items-center text-[12px]"
                 data-testid={`supervised-step-${s.key}`}>
              <span className={STATUS_TONE[s.status] || "text-muted-foreground"}>
                {STATUS_ICON[s.status] || "○"}
              </span>
              <span className={s.status === "WAITING" ? "text-muted-foreground" : ""}>
                {s.label}
                {s.detail && (
                  <span className="text-muted-foreground text-[10px] ml-2">
                    — {s.detail}
                  </span>
                )}
              </span>
              <span className={`mono text-[10px] uppercase tracking-widest ${STATUS_TONE[s.status]}`}>
                {s.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent runs */}
      {recent.length > 0 && (
        <div className="border border-border/60">
          <div className="p-2 label-cap border-b border-border/60">
            Recent Supervised Runs
          </div>
          <div className="divide-y divide-border/40">
            {recent.map(r => (
              <button
                key={r.id}
                onClick={() => setActiveRun(r)}
                className="w-full text-left p-2 text-sm hover:bg-border/10 flex items-center gap-3"
                data-testid={`recent-run-${r.id}`}>
                <span className={STATUS_TONE[r.overall] || "text-muted-foreground"}>
                  {STATUS_ICON[r.overall] || "○"}
                </span>
                <div className="flex-1">
                  <div className="text-xs">
                    {new Date(r.started_at).toLocaleString()} · Supervised Alpaca Paper
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {r.steps_completed || 0} / {(r.steps || []).length} steps
                    {r.duration_ms != null && ` · ${(r.duration_ms / 1000).toFixed(1)}s`}
                    {r.failure?.step && ` · Failure: ${r.failure.step}`}
                  </div>
                </div>
                <span className={`mono text-[10px] uppercase tracking-widest ${STATUS_TONE[r.overall]}`}>
                  {r.overall || r.status}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function PassSummary({ summary, onView }) {
  const fields = [
    ["Broker",       summary.broker],
    ["Symbol",       summary.symbol],
    ["Entry",        _fmt2(summary.entry_price)],
    ["Exit",         _fmt2(summary.exit_price)],
    ["Quantity",     summary.qty ?? "—"],
    ["Realized P&L", _fmt2(summary.realized_pnl)],
    ["Ledger",       summary.ledger_status || "—"],
    ["Ledger Drift", _fmt2(summary.ledger_drift)],
  ];
  return (
    <div className="border border-emerald-500/60 p-4"
         data-testid="supervised-pass-summary">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-cap">Supervised Paper Test</div>
          <div className="text-lg font-medium text-emerald-500">PASS</div>
        </div>
        {summary.harness_run_id && (
          <button
            onClick={onView} data-testid="supervised-view-full-timeline"
            className="border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-widest hover:bg-border/20">
            View Full Timeline
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        {fields.map(([k, v]) => (
          <div key={k} className="flex justify-between">
            <span className="text-muted-foreground">{k}</span>
            <span className="mono">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function FailureCard({ failure, harnessRunId, isBlocked, onRetry, onAckRetry }) {
  return (
    <div className={`border p-4 ${isBlocked ? "border-signal-yellow/60" : "border-red-500/60"}`}
         data-testid="supervised-failure-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-cap">Supervised Paper Test</div>
          <div className={`text-lg font-medium ${isBlocked ? "text-signal-yellow" : "pnl-down"}`}>
            {isBlocked ? "BLOCKED" : "FAILED"}
          </div>
        </div>
      </div>
      <div className="space-y-1.5 text-sm">
        <Row k="Step" v={failure.step} />
        {failure.sub_step && <Row k="Sub-step" v={failure.sub_step} />}
        <Row k="Reason" v={failure.reason} multiline />
        {failure.candidates_tried && (
          <Row k="Candidates tried" v={failure.candidates_tried.join(", ")} />
        )}
        {failure.recommended_action && (
          <div className="pt-2">
            <div className="label-cap mb-1">Recommended action</div>
            <div className="text-[12px] text-muted-foreground">
              {failure.recommended_action}
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-4">
        {isBlocked ? (
          <button
            onClick={onAckRetry}
            className="bg-signal-yellow text-black px-3 py-1.5 text-xs uppercase tracking-widest hover:opacity-90"
            data-testid="supervised-failure-ack-retry">
            Acknowledge & Retry
          </button>
        ) : (
          <button
            onClick={onRetry}
            className="bg-emerald-500 text-black px-3 py-1.5 text-xs uppercase tracking-widest hover:opacity-90"
            data-testid="supervised-failure-retry">
            Retry
          </button>
        )}
        {harnessRunId && (
          <button
            onClick={() => openAuthedReport(harnessRunId, "html")}
            className="border border-border/60 px-3 py-1.5 text-xs uppercase tracking-widest hover:bg-border/20"
            data-testid="supervised-failure-view-diagnostic">
            View Diagnostic
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ k, v, multiline }) {
  return (
    <div className={multiline ? "" : "flex justify-between gap-2"}>
      <span className="text-muted-foreground text-[11px] label-cap">{k}</span>
      <div className={multiline ? "mono text-[12px] mt-0.5" : "mono text-[12px]"}>{v}</div>
    </div>
  );
}
